/* Product-grid redesign (2026-07): one-click size add on product cards.
 *
 * When the size panel on a product card is open and the shopper picks a
 * size pill, submit the card's add-to-cart form immediately, then close
 * the panel (the theme's cart drawer opens on success as usual).
 *
 * 2026-08-11 fix — nothing is pre-selected any more.
 *   The theme renders the first available size with `checked` (Liquid:
 *   `option.selected_value == value`), so the panel opened with S already
 *   highlighted and a click on S produced no `change` event: the shopper
 *   had to pick another size and come back. The pick is now the shopper's
 *   own act: the panel is cleared every time it opens, and a click on a
 *   pill adds to the cart whether or not that pill was already selected.
 *
 * Progressive enhancement: without this file the panel still works —
 * the shopper picks a size and presses the Add to cart button below the
 * pills. Safe ordering is guaranteed because VariantPill's own 'change'
 * handler (global.js) updates the form's [name="id"] input synchronously
 * before this document-level listener runs.
 *
 * Guards:
 *  - only pills inside the card panel (.product-card-variants), never the
 *    color switcher on the photo;
 *  - only single-option products (e.g. Size only) — multi-option products
 *    need every option chosen, so they keep the explicit Add button and
 *    keep their pre-selection (global.js reads `:checked` of the previous
 *    option row to compute availability);
 *  - never fires for unavailable variants (disabled pill / disabled button);
 *  - sold-out pills cannot be selected at all, so the panel never shows a
 *    chosen size that leads nowhere.
 */
(function () {
  'use strict';
  if (window.__hhCardQuickAdd) return;
  window.__hhCardQuickAdd = true;

  /* lets CSS hide the redundant confirm button in single-option size panels */
  document.documentElement.classList.add('hh-quickadd');

  var PANEL = '.product-card-variants';
  var PANEL_PILL = PANEL + ' variant-pill';

  /* the card this element belongs to, but only when its panel holds exactly
     one option row (Size) — otherwise quick-add must not interfere */
  function singleOptionCard(el) {
    if (!el || !el.closest) return null;
    var card = el.closest('product-card');
    if (!card) return null;
    return card.querySelectorAll(PANEL_PILL).length === 1 ? card : null;
  }

  function panelRadios(card) {
    return card.querySelectorAll(PANEL_PILL + ' input[type="radio"]');
  }

  /* No default choice: clear whatever the server rendered as checked. The
     form keeps its [name="id"] value, but nothing is added until a pill is
     picked, and picking one updates the form first (VariantPill). */
  function clearSelection(card) {
    Array.prototype.forEach.call(panelRadios(card), function (input) {
      if (input.checked) input.checked = false;
    });
  }

  /* the panel is opened/closed by the theme's dropdown-toggler; this
     document-level listener runs after the toggler's own handler, so the
     --drawer-active class is already up to date */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('[data-btn-toggler]')) return;
    var card = singleOptionCard(e.target);
    if (!card || !card.classList.contains('--drawer-active')) return;
    clearSelection(card);
    /* a fresh panel is a fresh add — never let the previous pick's lock
       block a shopper who reopens the card to buy a second one */
    card.__hhAdding = false;
  });

  /* a sold-out size must stay unpickable — the confirm button is hidden on
     single-option cards, so a selected-but-unbuyable pill would be a dead end.
     Capture phase: preventDefault here stops the radio from being checked. */
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      var label = e.target.closest(PANEL + ' label.variant-element_label');
      if (!label || !singleOptionCard(label)) return;
      var input = label.previousElementSibling;
      if (input && input.type === 'radio' && input.classList.contains('disabled')) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  function quickAdd(input) {
    if (!input || input.type !== 'radio' || !input.closest) return;
    if (!input.closest(PANEL_PILL)) return;

    var card = singleOptionCard(input);
    if (!card || !card.classList.contains('--drawer-active')) return;
    if (input.classList.contains('disabled')) return;

    /* one pick fires both 'change' and 'click' — add once */
    if (card.__hhAdding) return;

    var atcButton = card.querySelector('atc-submit button[name="add"]');
    if (!atcButton || atcButton.disabled) return;

    card.__hhAdding = true;
    window.setTimeout(function () {
      card.__hhAdding = false;
    }, 900);

    atcButton.click();

    /* close the size panel; the cart drawer takes over */
    var closeToggler = card.querySelector('.variants-toggler--back');
    if (closeToggler) {
      window.setTimeout(function () {
        if (card.classList.contains('--drawer-active')) closeToggler.click();
      }, 250);
    }
  }

  /* 'change' is the normal path: VariantPill has already written the chosen
     variant into the form's [name="id"] by the time it bubbles here. */
  document.addEventListener('change', function (e) {
    quickAdd(e.target);
  });

  /* 'click' is the fallback for a pill that is already selected — no
     'change' fires then. It is deferred by one task because a browser
     dispatches 'click' on a radio BEFORE 'change': running it inline would
     add the previously selected variant. If a 'change' follows, it adds
     first and the lock makes this a no-op. */
  document.addEventListener('click', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio') return;
    window.setTimeout(function () {
      quickAdd(input);
    }, 0);
  });
})();
